import { ReactNode } from "react";
import PlaylistData from "../playlist/data/PlaylistData";
import { PlaylistSummary } from "./PlaylistSummary";
import { MultiSelectableList } from "../common/components/multiselectablelist/MultiSelectableList";
import { PlaylistIndexHeaderFirstRow } from "./PlaylistIndexHeaderFirstRow";
import { PlaylistIndexHeaderColumns } from "./PlaylistIndexHeaderColumns";
import { PlaylistRoutePaths } from "../playlist/PlaylistRoutePaths";
import { LookupSongFunction } from "../common/LookupSongFunction";

interface Props {
  playlistIndexData: readonly PlaylistData[];
  changeOrderBy: (newField: keyof PlaylistData) => void;
  showDetailsInPlaylistSummary: boolean;
  orderByAscOrDesc: "asc" | "desc";
  orderByField: keyof PlaylistData;
  performSearch: (searchTerm: string) => void;
  playlistIndexTableHeaderRef: React.RefObject<HTMLDivElement>;
  selectedPlaylists: readonly PlaylistData[];
  setSelectedPlaylists: (value: PlaylistData[]) => void;
  currentCrewIdentifier: string | undefined;
  setCurrentCrewIdentifier: (i: string | undefined) => void;
  performIndexRefresh: () => void;
  lookupSong: LookupSongFunction;
  setShowDetailsInPlaylistSummary: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  amountOfEntriesInPlaylistIndex: number;
  setAmountOfEntriesInPlaylistIndex: (
    value: ((prevState: number) => number) | number,
  ) => void;
  setIsMainMenuOpen: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
}

export function PlaylistIndexTable({
  playlistIndexData,
  changeOrderBy,
  showDetailsInPlaylistSummary,
  orderByAscOrDesc,
  orderByField,
  performSearch,
  playlistIndexTableHeaderRef,
  selectedPlaylists,
  setSelectedPlaylists,
  currentCrewIdentifier,
  setCurrentCrewIdentifier,
  performIndexRefresh,
  lookupSong,
  setShowDetailsInPlaylistSummary,
  amountOfEntriesInPlaylistIndex,
  setAmountOfEntriesInPlaylistIndex,
  setIsMainMenuOpen,
}: Props) {
  const headerFirstRow = (
    <PlaylistIndexHeaderFirstRow
      changeOrderBy={changeOrderBy}
      showDetailsInPlaylistSummary={showDetailsInPlaylistSummary}
      orderByAscOrDesc={orderByAscOrDesc}
      orderByField={orderByField}
      currentCrewIdentifier={currentCrewIdentifier}
      setCurrentCrewIdentifier={setCurrentCrewIdentifier}
      setShowDetailsInPlaylistSummary={setShowDetailsInPlaylistSummary}
      amountOfEntriesInPlaylistIndex={amountOfEntriesInPlaylistIndex}
      setAmountOfEntriesInPlaylistIndex={setAmountOfEntriesInPlaylistIndex}
      setIsMainMenuOpen={setIsMainMenuOpen}
    />
  );

  const headerColumns = (
    <PlaylistIndexHeaderColumns
      changeOrderBy={changeOrderBy}
      orderByAscOrDesc={orderByAscOrDesc}
      orderByField={orderByField}
      performSearch={performSearch}
      selectedPlaylists={selectedPlaylists}
      currentCrewIdentifier={currentCrewIdentifier}
      setCurrentCrewIdentifier={setCurrentCrewIdentifier}
      performIndexRefresh={performIndexRefresh}
    />
  );

  function renderRow(entry: PlaylistData): ReactNode {
    return (
      <PlaylistSummary
        key={entry.id}
        playlistData={entry}
        showDetailsInPlaylistSummary={showDetailsInPlaylistSummary}
        lookupSong={lookupSong}
      />
    );
  }

  return (
    <>
      <div ref={playlistIndexTableHeaderRef} />

      <MultiSelectableList
        headerFirstRow={headerFirstRow}
        headerColumns={headerColumns}
        data={playlistIndexData}
        renderElement={renderRow}
        keyExtractor={keyExtractor}
        linkBuilder={linkBuilder}
        setSelectedEntries={setSelectedPlaylists}
      />
    </>
  );
}

const keyExtractor = (d: PlaylistData) => d.id;

const linkBuilder = (d: PlaylistData) => PlaylistRoutePaths.toCarousel(d.id);
